import { Search, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { cn } from "~/lib/utils";

import { isFilterActive } from "../lib/courseFacets";

import { FilterChipGroup } from "./FilterChip";

import type { FilterChipOption } from "./FilterChip";
import type { CourseFacets, CourseFilters } from "../lib/courseFacets";

/**
 * Barra de servicio del catálogo público.
 *
 * ═══ Regla dura: en mobile esta barra mide 44px SIEMPRE ═══
 *
 * La versión anterior era un `flex-wrap`, y el alto se lo decidía el dato: con el catálogo de
 * staging (dos cursos gratis, una sola categoría) medía 44px, con el de prod (un gratis y un pago →
 * 3 chips de precio) 96px, y con 9 cursos y 2 categorías 252px. O sea: el arreglo del fold se
 * autodestruía en cuanto el catálogo crecía, justo lo que había venido a resolver — 252px de barra
 * son 56% de la primera pantalla en 390×844, casi el reclamo original entero.
 *
 * Ahora en mobile los controles viajan en UN riel horizontal de una sola fila
 * (`flex-nowrap overflow-x-auto`): entren 3 chips o 12, la barra ocupa una fila y nada más. Desktop
 * sigue wrapeando, que es donde hay ancho de sobra.
 *
 * Detalles del riel que no son decorativos:
 * - `[scrollbar-width:none]` + `[&::-webkit-scrollbar]:hidden` matan la barra de scroll gris del
 *   sistema (fea sobre el crema), pero NO el scroll: los chips siguen en el DOM, siguen siendo
 *   parada de tab y el browser los trae solos al foco. Nada se esconde de un lector de pantalla.
 * - `py-1 -my-1`: el `focus-visible:ring-2 ring-offset-2` de los chips pinta 4px POR FUERA del
 *   borde, y un contenedor con `overflow-x:auto` fuerza `overflow-y:auto` (regla del spec), así que
 *   sin esos 4px de aire el anillo de foco quedaba recortado arriba y abajo. El margen negativo
 *   devuelve los 4px, así que el riel sigue midiendo 44px por fuera. El wrapper es `flex` para que
 *   ese margen negativo no se colapse con el `mt-4` que viene por `className`.
 * - `mask-image` con un degradé sobre los últimos 40px: es la pista de "hay más a la derecha", y se
 *   eligió por sobre el "chip cortado a propósito" porque el corte depende de dónde caiga el borde
 *   del chip (si cae justo en el límite, no hay pista). El degradé se autocondiciona sin una línea de
 *   JS: si el contenido no llega al borde derecho, la zona del fade está vacía y no se ve nada.
 * - `pr-10` es la contraparte OBLIGATORIA del degradé, no decoración: le da al riel 40px que
 *   scrollear más allá del último chip, así que el último chip puede quedar FUERA de la franja
 *   atenuada. Sin eso, enfocarlo con el teclado mostraba un anillo de foco lavado (WCAG 2.4.11). La
 *   otra mitad del arreglo está en `FilterChip` (`scroll-mr-10` + un `scrollIntoView` explícito):
 *   el scroll implícito que hace el browser al enfocar NO alcanza — medido: con el chip parcialmente
 *   visible, Chrome no scrollea nada. Los dos chequeos viven en `scripts/qa-courses-facets.mjs`.
 * - `overscroll-x-contain`: sin esto, seguir arrastrando con el riel ya al tope dispara el gesto de
 *   "atrás" del browser y el usuario se va de la página queriendo mover unos chips.
 * - Sin `scroll-snap`: con roving tabindex el foco ya se mueve de chip en chip, y el snap peleaba
 *   con el scroll que hace el browser al enfocar.
 *
 * ═══ El orden de los ítems del riel no es decorativo ═══
 *
 * "Limpiar filtros" va PRIMERO, antes del buscador y de los chips. Con filtros activos que no
 * matchean nada, ese botón es la única salida del estado, y de último quedaba justo donde el degradé
 * de máscara lo atenúa o directamente fuera de la ventana del riel en 360px de ancho. Aparece sólo
 * con filtros activos, así que en el estado normal no le roba lugar a nada.
 *
 * ═══ Lo que NO está en esta barra ═══
 *
 * El ancla a "Próximamente" se fue: no es un filtro, es merchandising, y acá dentro era la causa
 * directa del salto de 44 a 96px (tiene 178px de ancho y con 3 chips no entra en 390px de
 * viewport). Vive en el renglón de servicio de `Courses.page.tsx`, que ya existía para el conteo de
 * resultados y no le suma alto al fold.
 *
 * Se fueron dos controles para siempre, no por espacio sino porque no filtraban:
 * - IDIOMA: cero cursos publicados en inglés en los tres ambientes (`available_locales` es `{es}` en
 *   2/2). Elegir "English" devolvía la grilla vacía Y además pisaba el idioma de presentación,
 *   pidiendo títulos en un idioma que no existe en la base.
 * - ORDEN: cuatro opciones para ordenar dos filas, y con un único precio distinto "menor a mayor" y
 *   "mayor a menor" devolvían la misma lista byte por byte. El orden pasó a ser regla de negocio
 *   (`defaultOrder`).
 *
 * DEUDA: con muchísimas categorías el riel se vuelve largo y scrollear 15 chips a mano es peor que
 * un bottom sheet. El umbral no está medido, así que no hay número acá: lo que sí está resuelto es
 * que el catálogo ya no puede empujar la primera tarjeta fuera del fold. Si algún día se hace el
 * sheet, van estos tres overrides ya verificados sobre `ui/sheet.tsx`: overlay
 * `bg-landing-ink-950/80` (el `bg-[color-mix(in_srgb,var(--color-black)_80%,transparent)]` del primitivo NO emite CSS — `colors.black` es
 * `var(--color-black)` sin `<alpha-value>`, así que el velo hoy es transparente), contenido
 * `bg-landing-white` (el `bg-background` cae al negro frío #08080c en oscuro) y `rounded-t-xl`
 * (la variante `bottom` sólo trae `border-t`).
 */
type CoursesFilterBarProps = {
  className?: string;
  filters: CourseFilters;
  facets: CourseFacets;
  onFilterChange: <K extends keyof CourseFilters>(name: K, value: CourseFilters[K]) => void;
  onClearFilters: () => void;
};

export function CoursesFilterBar({
  className,
  filters,
  facets,
  onFilterChange,
  onClearFilters,
}: CoursesFilterBarProps) {
  const { t } = useTranslation();

  const showCategories = facets.categories.length >= 2;
  const hasActiveFilters = isFilterActive(filters);
  // Todo lo que decide si esta barra existe sale de `availableCourses`, la MISMA query que decide si
  // se pintan skeletons o tarjetas. Antes también dependía de `useOpenPreinscriptionCourses` (por el
  // ancla), una query que no estaba en el `isLoading` de la página: resolvía después y la barra
  // aparecía de la nada empujando la grilla 60px.
  const hasAnything = facets.search || facets.price || showCategories || hasActiveFilters;

  const priceOptions = useMemo<FilterChipOption[]>(
    () => [
      { value: "all", label: t("landing.courses.filters.allPrices") },
      { value: "free", label: t("landing.courses.filters.free") },
      { value: "paid", label: t("landing.courses.filters.paid") },
    ],
    [t],
  );

  const categoryOptions = useMemo<FilterChipOption[]>(
    () => [
      { value: "", label: t("landing.courses.filters.allCategories") },
      ...facets.categories.map((category) => ({ value: category, label: category })),
    ],
    [facets.categories, t],
  );

  // Sin nada que ofrecer, la barra no ocupa ni un píxel del fold.
  if (!hasAnything) return null;

  return (
    // Los `data-qa` no son decoración: el QA mide el ALTO DE ESTA BARRA, y hasta ahora la encontraba
    // por la forma del DOM (`[role=radiogroup]` → parentElement → parentElement). Eso devolvía 0
    // cuando el único control de la barra es el botón de limpiar (catálogo de staging con un filtro
    // activo), o sea el script informaba "barHeight 0" con 44px de barra en pantalla. Con el hook
    // explícito, la medición no depende de qué controles haya adentro.
    <div data-qa="filter-bar" className={cn("flex", className)}>
      <div
        data-qa="filter-rail"
        className={cn(
          "-my-1 flex min-w-0 flex-1 flex-nowrap items-center gap-2 overflow-x-auto overscroll-x-contain py-1 pr-10",
          "[mask-image:linear-gradient(to_right,#000_calc(100%_-_40px),transparent)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          "sm:flex-wrap sm:overflow-x-visible sm:pr-0 sm:[mask-image:none]",
        )}
      >
        {hasActiveFilters && (
          // ═══ PRIMER ítem del riel, no el último ═══
          // Estaba al final, y con filtros activos era la única salida de un "0 de 2 cursos": justo
          // lo que el degradé de máscara atenúa o lo que queda fuera de la ventana del riel en 360px.
          // Un estado sin salida visible no es un estado, es una trampa. Primero también le conviene
          // al teclado: es la primera parada de tab de la barra.
          // `brand-outline` en vez de `ghost`: el ghost del primitivo es `hover:bg-accent`, o sea la
          // pastilla celeste #e1eaf8 del admin, y para pisarla no alcanza con cambiar el color de
          // texto. `bg-landing-white` porque la variante trae `bg-white`, que en oscuro cae al negro
          // frío. `size="lg"` da los 44px; el `px-8` que arrastra se pisa con `px-4`.
          <Button
            variant="brand-outline"
            size="lg"
            onClick={onClearFilters}
            data-qa="clear-filters"
            className="h-11 shrink-0 gap-1.5 bg-landing-white px-4 focus-visible:ring-landing-red focus-visible:ring-offset-landing-cream"
          >
            <X aria-hidden="true" className="size-4" />
            {t("landing.courses.filters.clearFilters")}
          </Button>
        )}

        {facets.search && (
          <div className="relative w-[180px] shrink-0 sm:w-auto sm:min-w-[180px] sm:max-w-xs sm:flex-1">
            <label htmlFor="courses-search" className="sr-only">
              {t("landing.courses.filters.searchLabel")}
            </label>
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-landing-stone-400"
            />
            {/* El primitivo `Input` trae `h-[42px]`, borde gris frío del admin y
                `focus-visible:outline-none` SIN ring de reemplazo (falla WCAG 2.4.7). Se pisa todo
                por className: `cn(base, className)` + tailwind-merge desduplica. No se edita el
                primitivo porque lo comparten el admin y seis pantallas internas. */}
            <Input
              id="courses-search"
              type="search"
              placeholder={t("landing.courses.searchPlaceholder")}
              value={filters.searchQuery}
              onChange={(e) => onFilterChange("searchQuery", e.target.value)}
              className="h-11 rounded-lg border-landing-border bg-landing-white pl-10 text-[15px] text-landing-text placeholder:text-landing-light focus-visible:border-landing-red focus-visible:ring-2 focus-visible:ring-landing-red-50"
            />
          </div>
        )}

        {facets.price && (
          <FilterChipGroup
            label={t("landing.courses.filters.price")}
            options={priceOptions}
            value={filters.priceFilter}
            onChange={(value) =>
              onFilterChange("priceFilter", value as CourseFilters["priceFilter"])
            }
          />
        )}

        {showCategories && (
          <FilterChipGroup
            label={t("landing.courses.filters.category")}
            options={categoryOptions}
            value={filters.category}
            onChange={(value) => onFilterChange("category", value)}
          />
        )}
      </div>
    </div>
  );
}
